import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Pencil,
  Calendar,
  User as UserIcon,
  ImageIcon,
  Camera,
  OctagonX,
  Trash2,
  Check,
  X,
  AlertTriangle,
} from 'lucide-react';
import { eventsApi } from '../../api/events';
import { Event, EventManagerPermissions } from '../../types';
import { Button } from '../UI/Button';
import { Modal } from '../UI/Modal';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { track, EVENTS } from '../../lib/mixpanel';
import { clayStaggerContainer, clayStaggerItem, clayTapScale } from '../../constants/animations';
import { useAuthStore } from '../../store/authStore';
import styles from './EventSettingsPanel.module.css';

interface EventSettingsPanelProps {
  event: Event;
  onEventUpdated: (event: Event) => void;
  permissions: EventManagerPermissions;
}

// ─── Inline-editable field ────────────────────────────────────────────────────

function EditableField({
  label,
  value,
  placeholder,
  multiline,
  disabled,
  onSave,
}: {
  label: string;
  value: string;
  placeholder: string;
  multiline?: boolean;
  disabled?: boolean;
  onSave: (value: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (trimmed === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  if (editing) {
    return (
      <div>
        <label className={styles['field-label']}>
          {label}
        </label>
        {multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={3}
            disabled={saving}
            className={[styles['field-input'], styles['field-input-textarea']].join(' ')}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={saving}
            className={styles['field-input']}
          />
        )}
        <div className={styles['field-actions']}>
          <motion.button
            onClick={handleSave}
            disabled={saving}
            className={styles['save-btn']}
            whileTap={clayTapScale}
          >
            <Check className={styles['icon-sm']} />
            Save
          </motion.button>
          <motion.button
            onClick={handleCancel}
            disabled={saving}
            className={styles['cancel-btn']}
            whileTap={clayTapScale}
          >
            <X className={styles['icon-sm']} />
            Cancel
          </motion.button>
        </div>
      </div>
    );
  }

  // ── Read-only state ──
  return (
    <div className={styles['field-group']}>
      <label className={styles['field-label']}>
        {label}
      </label>
      {disabled ? (
        <p className={[styles['read-only-text'], !value ? styles['read-only-placeholder'] : ''].filter(Boolean).join(' ')}>
          {value || placeholder}
        </p>
      ) : (
        <motion.button
          onClick={() => { setDraft(value); setEditing(true); }}
          className={[
            styles['read-only-btn'],
            !value ? styles['read-only-placeholder'] : styles['read-only-text'],
          ].join(' ')}
          whileTap={{ scale: 0.995 }}
        >
          <span className={styles['read-only-inner']}>
            <span className={multiline ? styles['multiline-text'] : styles['truncate-text']}>
              {value || placeholder}
            </span>
            <Pencil className={`${styles['icon-sm']} ${styles['pencil-icon']}`} />
          </span>
        </motion.button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const EventSettingsPanel: React.FC<EventSettingsPanelProps> = ({ event, onEventUpdated, permissions }) => {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const isActive = event.status === 'active';
  const createdBy = typeof event.createdBy === 'object' ? event.createdBy.name : 'Unknown';
  const isEventOwner =
    currentUser != null &&
    (typeof event.createdBy === 'object'
      ? event.createdBy._id === currentUser._id
      : event.createdBy === currentUser._id);

  const canEditDetails = isEventOwner || permissions.allowEditingEventDetails;
  const canEndEvent = isEventOwner || permissions.allowEndingEvent;
  const canDeleteEvent = isEventOwner || permissions.allowDeletingEvent;

  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [ending, setEnding] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleUpdateField = async (field: 'name' | 'description', value: string) => {
    if (field === 'name' && !value) {
      toast.error('Event name cannot be empty');
      return;
    }
    try {
      const updated = await eventsApi.update(event._id, { [field]: value });
      onEventUpdated(updated);
      track(EVENTS.EVENT_UPDATED, { event_id: event._id, field });
      toast.success(`Event ${field} updated`);
    } catch {
      toast.error(`Failed to update ${field}`);
    }
  };

  const handleEndEvent = async () => {
    setEnding(true);
    try {
      const updated = await eventsApi.end(event._id);
      onEventUpdated(updated);
      setShowEndConfirm(false);
      track(EVENTS.EVENT_ENDED, { event_id: event._id, event_name: event.name });
      toast.success('Event ended successfully');
    } catch {
      toast.error('Failed to end event');
    } finally {
      setEnding(false);
    }
  };

  const handleDeleteEvent = async () => {
    setDeleting(true);
    try {
      await eventsApi.delete(event._id);
      track(EVENTS.EVENT_DELETED, {
        event_id: event._id,
        event_name: event.name,
        photo_count: event.photoCount,
      });
      toast.success('Event deleted permanently');
      navigate('/events');
    } catch {
      toast.error('Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <motion.div
      className={styles['root']}
      variants={clayStaggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* ── Section 1: Event Details ──────────────────────────────────── */}
      <motion.div
        variants={clayStaggerItem}
        className={styles['section-card']}
      >
        <div className={styles['section-body']}>
          <EditableField
            label="Event Name"
            value={event.name}
            placeholder="Untitled Event"
            disabled={!isActive || !canEditDetails}
            onSave={(v) => handleUpdateField('name', v)}
          />

          <EditableField
            label="Description"
            value={event.description}
            placeholder="Add a description..."
            multiline
            disabled={!isActive || !canEditDetails}
            onSave={(v) => handleUpdateField('description', v)}
          />

          <div className={styles['stat-grid']}>
            <StatCard icon={ImageIcon} label="Photos" value={event.photoCount != null ? String(event.photoCount) : '--'} />
            <StatCard icon={Camera} label="Cameras" value={isActive ? (event.ftpAccountCount != null ? String(event.ftpAccountCount) : '--') : '0'} />
            <StatCard icon={Calendar} label="Created" value={formatDateShort(event.createdAt)} />
            <StatCard icon={UserIcon} label="Owner" value={createdBy} />
          </div>

          {event.endedAt && (
            <div className={styles['ended-banner']}>
              <OctagonX className={styles['ended-icon']} />
              <p className={`${styles['ended-text']} ${styles['ended-text-red']}`}>
                Event ended on {formatDate(event.endedAt)}
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Section 2: Danger Zone ── */}
      {((isActive && canEndEvent) || canDeleteEvent) && (
        <motion.div
          variants={clayStaggerItem}
          className={styles['danger-card']}
        >
          <div className={styles['danger-header']}>
            <AlertTriangle className={styles['danger-header-icon']} />
            <h2 className={`${styles['danger-title']} ${styles['danger-title-red']}`}>Danger Zone</h2>
          </div>

          <div className={styles['danger-body']}>
            {isActive && canEndEvent && (
              <div className={styles['danger-action-row']}>
                <div className={styles['danger-action-text']}>
                  <p className={styles['danger-action-title']}>End this event</p>
                  <p className={styles['danger-action-desc']}>Deletes all FTP accounts. Photographers lose upload access. Photos are preserved.</p>
                </div>
                <motion.button
                  onClick={() => setShowEndConfirm(true)}
                  className={styles['end-btn']}
                  whileTap={clayTapScale}
                >
                  <OctagonX className={styles['icon-sm']} />
                  End Event
                </motion.button>
              </div>
            )}

            {canDeleteEvent && (
              <div className={styles['danger-action-row']}>
                <div className={styles['danger-action-text']}>
                  <p className={styles['danger-action-title']}>Delete this event</p>
                  <p className={styles['danger-action-desc']}>
                    Permanently removes {event.photoCount ?? 'all'} photos, overlays, presets, albums, and accounts.
                  </p>
                </div>
                <motion.button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={styles['delete-btn']}
                  whileTap={clayTapScale}
                >
                  <Trash2 className={styles['icon-sm']} />
                  Delete Event
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ── End Event Modal ── */}
      <Modal isOpen={showEndConfirm} onClose={() => setShowEndConfirm(false)} title="End Event">
        <div className={styles['modal-body']}>
          <motion.div
            className={styles['modal-character']}
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1, rotate: [14, 20, 14] }}
            transition={{
              rotate: { repeat: Infinity, duration: 2.5, ease: 'easeInOut' },
              default: { duration: 0.35 },
            }}
          >
            <MachhaCharacter state="Warning" part="face" size={72} />
          </motion.div>
          <p className={styles['modal-text']}>
            Are you sure you want to end{' '}
            <strong className={styles['modal-strong']}>{event.name}</strong>?
          </p>
          <ul className={styles['modal-list']}>
            <li>All FTP accounts will be <strong className={styles['modal-strong-red']}>permanently deleted</strong></li>
            <li>Photographers will immediately lose upload access</li>
            <li>Photos, overlays, and presets are preserved</li>
          </ul>
          <div className={styles['modal-actions']}>
            <Button variant="ghost" onClick={() => setShowEndConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={ending} onClick={handleEndEvent}>
              End Event
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Delete Event Modal ── */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteConfirmName('');
        }}
        title="Delete Event Permanently"
      >
        <div className={styles['modal-body']}>
          <motion.div
            className={styles['modal-character']}
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1, rotate: [-8, -12, -8] }}
            transition={{
              rotate: { repeat: Infinity, duration: 3, ease: 'easeInOut' },
              default: { duration: 0.35 },
            }}
          >
            <MachhaCharacter state="Error" part="face" size={72} />
          </motion.div>
          <p className={styles['modal-text']}>
            This will permanently delete{' '}
            <strong className={styles['modal-strong-red']}>{event.name}</strong> and all associated data:
          </p>
          <ul className={styles['modal-list']}>
            <li>{event.photoCount ?? 'All'} photos (removed from S3)</li>
            <li>All overlays and export presets</li>
            <li>All albums and their members</li>
            <li>All FTP accounts</li>
          </ul>
          <p className={styles['delete-confirm-label']}>
            Type <strong className={styles['modal-strong']}>{event.name}</strong> to confirm:
          </p>
          <input
            type="text"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            placeholder={event.name}
            className={styles['delete-confirm-input']}
          />
          <div className={styles['modal-actions']}>
            <Button
              variant="ghost"
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeleteConfirmName('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleting}
              disabled={deleteConfirmName !== event.name}
              onClick={handleDeleteEvent}
            >
              <Trash2 className={styles['icon-sm']} />
              Delete Forever
            </Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
};

// ─── Sub-components ────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.FC<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
}) {
  return (
    <div className={styles['stat-card']}>
      <Icon className={`${styles['icon-sm']} ${styles['stat-icon']}`} />
      <p className={styles['stat-value']}>
        {value}
      </p>
      <p className={styles['stat-label']}>
        {label}
      </p>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
